import { useState, useEffect, useCallback } from "react";
import { Link, useNavigate } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { Plus, Edit, Trash2, Eye, EyeOff, FileText, Search, Clock, CheckCircle2, FolderTree, Tag as TagIcon } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const StatusPill = ({ status }) => {
  const map = {
    draft: { cls: "bg-slate-100 text-slate-600", label: "draft", Icon: EyeOff },
    scheduled: { cls: "bg-amber-100 text-amber-700", label: "scheduled", Icon: Clock },
    published: { cls: "bg-prepify-100 text-prepify-700", label: "published", Icon: CheckCircle2 },
  };
  const { cls, label, Icon } = map[status] || map.draft;
  return <span className={`inline-flex items-center gap-1 text-xs font-medium px-2 py-0.5 rounded-full ${cls}`}><Icon size={11} /> {label}</span>;
};

const BlogList = () => {
  const [posts, setPosts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [statusFilter, setStatusFilter] = useState("");
  const [query, setQuery] = useState("");
  const [categoryId, setCategoryId] = useState("");
  const [tagId, setTagId] = useState("");
  const [categories, setCategories] = useState([]);
  const [tags, setTags] = useState([]);
  const nav = useNavigate();

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = {};
      if (statusFilter) params.status = statusFilter;
      if (query) params.q = query;
      if (categoryId) params.category_id = categoryId;
      if (tagId) params.tag_id = tagId;
      const res = await axios.get(`${API}/cms/blog`, { params });
      setPosts(res.data);
    } catch {
      toast.error("Failed to load posts");
    } finally {
      setLoading(false);
    }
  }, [statusFilter, query, categoryId, tagId]);

  useEffect(() => {
    const t = setTimeout(load, query ? 300 : 0);
    return () => clearTimeout(t);
  }, [load, query]);

  useEffect(() => {
    axios.get(`${API}/cms/blog-categories`).then((r) => setCategories(r.data)).catch(() => {});
    axios.get(`${API}/cms/blog-tags`).then((r) => setTags(r.data)).catch(() => {});
  }, []);

  const createNew = async () => {
    try {
      const res = await axios.post(`${API}/cms/blog`, { title: "Untitled post", excerpt: "" });
      nav(`/app/website/blog/${res.data.id}`);
    } catch {
      toast.error("Could not create post");
    }
  };

  const togglePublish = async (p) => {
    const ep = p.status === "published" ? "unpublish" : "publish";
    await axios.post(`${API}/cms/blog/${p.id}/${ep}`);
    toast.success(p.status === "published" ? "Unpublished" : "Published");
    load();
  };

  const remove = async (id) => {
    if (!window.confirm("Delete this post permanently?")) return;
    await axios.delete(`${API}/cms/blog/${id}`);
    toast.success("Deleted");
    load();
  };

  const catById = (id) => categories.find((c) => c.id === id);
  const tagById = (id) => tags.find((t) => t.id === id);

  return (
    <div className="max-w-6xl mx-auto space-y-6">
      <header className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h1 className="text-2xl font-bold text-slate-900">Blog</h1>
          <p className="text-sm text-slate-500">Manage blog posts published at /blog.</p>
        </div>
        <div className="flex flex-wrap gap-2">
          <Link to="/app/website/blog-categories" className="inline-flex items-center gap-2 border border-slate-200 hover:bg-slate-50 text-slate-700 font-medium px-3.5 py-2 rounded-lg text-sm" data-testid="manage-categories-link">
            <FolderTree size={14} /> Categories
          </Link>
          <Link to="/app/website/blog-tags" className="inline-flex items-center gap-2 border border-slate-200 hover:bg-slate-50 text-slate-700 font-medium px-3.5 py-2 rounded-lg text-sm" data-testid="manage-tags-link">
            <TagIcon size={14} /> Tags
          </Link>
          <button onClick={createNew} className="inline-flex items-center gap-2 bg-prepify-600 hover:bg-prepify-700 text-white font-medium px-4 py-2 rounded-lg text-sm" data-testid="new-post-btn">
            <Plus size={14} /> New post
          </button>
        </div>
      </header>

      <div className="bg-white border border-slate-200 rounded-xl p-3 flex flex-wrap items-center gap-2">
        <div className="relative flex-1 min-w-[200px] max-w-md">
          <Search size={14} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-slate-400" />
          <input
            placeholder="Search by title…"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            className="w-full border border-slate-200 rounded-lg pl-8 pr-3 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-prepify-500/40"
            data-testid="blog-search-input"
          />
        </div>
        <select value={categoryId} onChange={(e) => setCategoryId(e.target.value)} className="text-sm border border-slate-200 rounded-lg px-2.5 py-1.5 bg-white focus:outline-none focus:ring-2 focus:ring-prepify-500/40" data-testid="blog-category-filter">
          <option value="">All categories</option>
          {categories.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
        </select>
        <select value={tagId} onChange={(e) => setTagId(e.target.value)} className="text-sm border border-slate-200 rounded-lg px-2.5 py-1.5 bg-white focus:outline-none focus:ring-2 focus:ring-prepify-500/40" data-testid="blog-tag-filter">
          <option value="">All tags</option>
          {tags.map((t) => <option key={t.id} value={t.id}>#{t.name}</option>)}
        </select>
        <div className="flex gap-1 ml-auto">
          {[["All", ""], ["Drafts", "draft"], ["Scheduled", "scheduled"], ["Published", "published"]].map(([lbl, val]) => (
            <button key={val} onClick={() => setStatusFilter(val)} className={`text-xs px-3 py-1.5 rounded-full border transition ${statusFilter === val ? "bg-slate-900 text-white border-slate-900" : "bg-white text-slate-600 border-slate-200 hover:border-slate-300"}`} data-testid={`status-filter-${val || "all"}`}>{lbl}</button>
          ))}
        </div>
      </div>

      {loading ? (
        <div className="text-slate-400">Loading…</div>
      ) : posts.length === 0 ? (
        <div className="text-slate-400 text-center py-16 border-2 border-dashed border-slate-200 rounded-xl">
          No posts found. Click <b>New post</b> to write your first article, or adjust filters.
        </div>
      ) : (
        <div className="bg-white border border-slate-200 rounded-xl overflow-hidden">
          <table className="w-full text-sm">
            <thead className="bg-slate-50 border-b border-slate-200">
              <tr>
                <th className="text-left px-4 py-3 font-semibold">Title</th>
                <th className="text-left px-4 py-3 font-semibold">Status</th>
                <th className="text-left px-4 py-3 font-semibold">Categories</th>
                <th className="text-left px-4 py-3 font-semibold">Updated</th>
                <th className="text-right px-4 py-3 font-semibold">Actions</th>
              </tr>
            </thead>
            <tbody>
              {posts.map((p) => (
                <tr key={p.id} className="border-b border-slate-100 hover:bg-slate-50/60" data-testid={`post-row-${p.id}`}>
                  <td className="px-4 py-3">
                    <Link to={`/app/website/blog/${p.id}`} className="font-medium text-slate-900 hover:text-prepify-600 inline-flex items-center gap-2">
                      <FileText size={14} className="text-slate-400" /> {p.title}
                    </Link>
                    <div className="text-xs text-slate-400 font-mono mt-0.5">/{p.slug}</div>
                  </td>
                  <td className="px-4 py-3"><StatusPill status={p.status} /></td>
                  <td className="px-4 py-3">
                    <div className="flex flex-wrap gap-1">
                      {(p.category_ids || []).slice(0, 2).map((cid) => {
                        const c = catById(cid);
                        return c ? <span key={cid} className="text-[10px] font-medium px-2 py-0.5 rounded-full text-white" style={{ background: c.color || "#0057D9" }}>{c.name}</span> : null;
                      })}
                      {(p.tag_ids || []).slice(0, 2).map((tid) => {
                        const t = tagById(tid);
                        return t ? <span key={tid} className="text-[10px] text-slate-600 bg-slate-100 px-2 py-0.5 rounded-full">#{t.name}</span> : null;
                      })}
                    </div>
                  </td>
                  <td className="px-4 py-3 text-slate-500 text-xs">{new Date(p.updated_at).toLocaleString()}</td>
                  <td className="px-4 py-3 text-right">
                    <div className="inline-flex gap-1">
                      <button onClick={() => togglePublish(p)} className="text-xs px-2.5 py-1 border border-slate-200 rounded hover:bg-slate-50" data-testid={`toggle-publish-${p.id}`}>
                        {p.status === "published" ? "Unpublish" : "Publish"}
                      </button>
                      <Link to={`/app/website/blog/${p.id}`} className="text-xs px-2.5 py-1 border border-slate-200 rounded hover:bg-slate-50 inline-flex items-center gap-1"><Edit size={12} /></Link>
                      <button onClick={() => remove(p.id)} className="text-xs px-2.5 py-1 border border-slate-200 rounded hover:bg-red-50 hover:text-red-600 hover:border-red-200" data-testid={`delete-post-${p.id}`}><Trash2 size={12} /></button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
};

export default BlogList;
